<template>
    <v-card>
        <v-card-title class="px-6 pt-4">
            {{ $t('history_of_change_title') }}
        </v-card-title>
        <v-icon class="v-card-close" @click="$emit('close-modal')">mdi-close</v-icon>
        <v-card-text>
            <filters-wrapper :has-active-filters="hasActiveFilters" @submit="applyFilter" @reset="resetFilter">
                <v-form class="table-filters">
                    <v-row dense>
                        <v-col cols="12" md="4">
                            <daterange
                                :start-date-label="$t('date_from')"
                                :end-date-label="$t('date_to')"
                                :dense="true"
                                :single-line="false"
                                :hide-details="false"
                                :min-start-date="null"
                                same-date
                                :_start-date="filters.dateFrom"
                                :_end-date="filters.dateTo"
                                data-cy="date-range"
                                :clearable="true"
                                @change-start-date="filters.dateFrom = $event"
                                @change-end-date="filters.dateTo = $event"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <v-text-field
                                v-model="filters.serviceName"
                                :label="$t('service_name')"
                                hide-details
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="name"
                                class="mb-4"
                            />
                        </v-col>
                        <v-col cols="12" md="3">
                            <person-select-autocomplete
                                v-model="filters.userId"
                                :item-value="'personId'"
                                :company-id="authCompanyId"
                                outlined
                                dense
                                preload-all
                                closable-chips
                                disable-persistent-placeholder
                                class="mb-4"
                                :rules="[]"
                                :label="$t('user')"
                            />
                        </v-col>
                        <v-col cols="12" md="2">
                            <v-select
                                v-model="filters.operation"
                                :items="operations"
                                :label="$t('operation')"
                                clearable
                                density="compact"
                                variant="outlined"
                                color="primary"
                                data-cy="type"
                                hide-details
                                :loading="isLoadingOperations"
                                class="mb-4"
                            />
                        </v-col>
                    </v-row>
                </v-form>
            </filters-wrapper>
            <v-data-table-server
                v-model:page="activePage"
                v-model:items-per-page="itemsPerPage"
                :headers="headers"
                :items="entries"
                :loading="isLoading"
                :items-length="itemsLength"
                :hide-default-header="isMobile"
                :mobile-breakpoint="0"
                :items-per-page-options="[
                    {value: itemsPerPage, title: itemsPerPage},
                    {value: 25, title: '25'},
                    {value: 50, title: '50'},
                    {value: 100, title: '100'},
                ]"
                class="elevation-1 mt-1"
                @update:options="loadItems"
            >
                <template v-slot:item.time="{item}">
                    {{ $filters.dateTimeFormat(item.time) }}
                </template>
                <template v-slot:item.serviceName="{item}">
                    {{ getServiceName(item) }}
                </template>
                <template v-slot:item.user="{item}">
                    <div v-if="item.user?.login">{{ item.user.login }}</div>
                    <div v-if="item.user?.name">{{ item.user.name }}</div>
                    <div v-if="item.user?.companyName" class="text-grey">{{ `(${item.user.companyName})` }}</div>
                </template>
                <template v-slot:item.operationResult="{item}">
                    <div style="white-space: pre-wrap" class="py-4" v-html="item.operationResult" />
                </template>
                <template v-slot:item.mobile="{item}">
                    <div class="py-2 d-flex full-width">
                        <div class="d-flex flex-column flex-grow-1 flex-shrink-1 overflow-hidden">
                            <div>{{ $filters.dateTimeFormat(item.time) }}</div>
                            <div>{{ getServiceName(item) }}</div>
                            <div style="white-space: pre-wrap" v-html="item.operationResult" />
                        </div>
                        <div class="pl-2 d-flex flex-column flex-grow-0 flex-shrink-0 align-end overflow-hidden">
                            <div v-if="item.user?.login">{{ item.user.login }}</div>
                            <div v-if="item.user?.name">{{ item.user.name }}</div>
                            <div v-if="item.user?.companyName" class="text-grey">
                                {{ `(${item.user.companyName})` }}
                            </div>
                        </div>
                    </div>
                </template>
            </v-data-table-server>
        </v-card-text>
    </v-card>
</template>

<script lang="ts">
    import {Component, Prop, Vue} from 'vue-facing-decorator'
    import Daterange from '~/components/search/forms/Daterange.vue'
    import PersonSelectAutocomplete from '~/components/search/forms/PersonSelectAutocomplete.vue'

    @Component({
        components: {PersonSelectAutocomplete, Daterange},
    })
    export class OrderHistory extends Vue {
        @Prop() orderId

        isLoading = false
        isLoadingOperations = false

        entries = []
        itemsLength = 0
        itemsPerPage = 10
        activePage = 1

        operations = []
        filters = {}

        async mounted() {
            await this.loadOperations()
        }

        async loadOperations() {
            try {
                this.isLoadingOperations = true

                const {operations} = await this.$api.orderHistoryOperations.get()
                this.operations = operations.sort((a, b) => {
                    return a.localeCompare(b)
                })

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoadingOperations = false
            }
        }

        async loadItems({itemsPerPage, page}) {
            try {
                this.isLoading = true

                const {entries, count} = await this.$api.orderHistory.get({
                    orderId: this.orderId,
                    ...clean(this.filters),
                    limit: itemsPerPage,
                    offset: itemsPerPage * (page - 1),
                })
                this.entries = entries
                this.itemsLength = count

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isLoading = false
            }
        }

        getServiceName(item) {
            return item.serviceName.trim().replace('extraService', '')
        }

        get headers() {
            if (this.isMobile) return [{value: 'mobile'}]

            return [
                {title: this.$t('date_time'), value: 'time'},
                {title: this.$t('order_service'), value: 'serviceName'},
                {title: this.$t('user'), value: 'user'},
                {title: this.$t('operation'), value: 'operation'},
                {title: this.$t('operation_result'), value: 'operationResult'},
            ]
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        resetFilter() {
            this.activePage = 1
            this.filters = {}
            this.loadItems({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        applyFilter() {
            this.activePage = 1
            this.loadItems({itemsPerPage: this.itemsPerPage, page: this.activePage})
        }

        get hasActiveFilters() {
            return Object.keys(clean(this.filters)).length !== 0
        }

        get authCompanyId() {
            return authStore.company?.id
        }
    }

    export default OrderHistory
</script>
